import type {PrivacyRequestDto, PrivacyRequestStatus} from '@devmil/contracts';
import {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';

import {PageTitle} from '../components/PageTitle.js';
import {PrivacyDeliveryStatus} from '../features/privacy/PrivacyDeliveryStatus.js';
import {PrivacyRequestForm} from '../features/privacy/PrivacyRequestForm.js';
import {PrivacyResolutionForm} from '../features/privacy/PrivacyResolutionForm.js';
import {PrivacyVerificationPanel} from '../features/privacy/PrivacyVerificationPanel.js';
import {
  usePrivacyRefresh,
  usePrivacyRequest,
  usePrivacyRequests,
} from '../features/privacy/usePrivacyRequests.js';
import {formatDateTime} from '../lib/format.js';
import {describeProblem} from '../lib/panel-problem.js';

/**
 * Pedidos de privacidade da LGPD.
 *
 * Tela nova, fora da prévia aprovada, herdando tokens e classes de
 * `app/styles.css`. Ela não entra em `PANEL_ROUTES`: a navegação da esquerda é
 * parte do pixel aprovado das quatro telas, e um item novo lá muda as quatro de
 * uma vez. Chega-se aqui pela URL e pelo detalhe de uma conversa.
 */

const DIREITO_LABEL: Record<PrivacyRequestDto['right'], string> = {
  access: 'Acesso',
  correction: 'Correção',
  opposition: 'Oposição',
  consent_revocation: 'Revogação',
  erasure: 'Exclusão',
};

const ESTADO_LABEL: Record<PrivacyRequestStatus, string> = {
  received: 'RECEBIDO',
  verification_pending: 'AGUARDA TITULAR',
  verified: 'TITULAR VERIFICADO',
  executing: 'EXECUTANDO',
  delivery_ready: 'ENTREGA PRONTA',
  completed: 'CONCLUÍDO',
  rejected: 'RECUSADO',
};

export const PrivacyRequestsPage = () => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [verifyOpen, setVerifyOpen] = useState(false);

  const lista = usePrivacyRequests();
  const detalhe = usePrivacyRequest(selectedId);
  const refresh = usePrivacyRefresh(selectedId);

  // Trocar de pedido fecha a verificação: um formulário aberto para um pedido
  // não pode ser enviado sobre outro.
  useEffect(() => {
    setVerifyOpen(false);
  }, [selectedId]);

  const cabecalho = (
    <PageTitle
      title="Pedidos de privacidade"
      subtitle="Direitos do titular. Sua autenticação no painel não é a prova do titular."
      badge="PROVA DO TITULAR"
    />
  );

  return (
    <section data-page="privacy-requests">
      {cabecalho}

      <div className="dm-actions">
        <Link className="dm-action" to="/mapa">
          Voltar ao funil
        </Link>
      </div>

      <div className="dm-ops-grid">
        <section className="dm-panel" aria-label="Pedidos recebidos">
          <div className="dm-panel-head">
            <h2>Pedidos recebidos</h2>
            <span className="dm-illustrative">
              {lista.data
                ? // O `+` quando existe página seguinte: a API devolve 25 por
                  // vez, e "25 EM ABERTO" com mais pedidos atrás seria contagem
                  // apresentada como medição. A lista já vem só com pedidos em
                  // aberto, ordenada pelo prazo mais perto de vencer.
                  `${lista.data.items.length}${lista.data.nextCursor ? '+' : ''} EM ABERTO`
                : 'LENDO'}
            </span>
          </div>

          {lista.isPending ? (
            <p className="dm-subtitle" role="status">
              Carregando os pedidos…
            </p>
          ) : null}

          {lista.error ? (
            <div className="dm-alert" role="alert">
              {describeProblem(lista.error).message}
            </div>
          ) : null}

          {lista.data && lista.data.items.length === 0 ? (
            <p className="dm-subtitle">
              Nenhum pedido registrado. Vazio aqui é ausência de pedido, não
              pedido perdido: todo pedido chega por Instagram ou WhatsApp e é
              aberto à mão.
            </p>
          ) : null}

          <div className="dm-queue">
            {lista.data?.items.map((pedido) => (
              <button
                key={pedido.id}
                className="dm-request"
                type="button"
                aria-pressed={pedido.id === selectedId}
                onClick={() => {
                  setSelectedId(pedido.id);
                }}
              >
                <span>
                  <strong className="dm-piece-title">
                    {DIREITO_LABEL[pedido.right]}
                  </strong>
                  <small className="dm-piece-meta">
                    {`Prazo ${formatDateTime(pedido.dueAt)}`}
                  </small>
                </span>
                <span
                  className={
                    pedido.subjectVerifiedAt
                      ? 'dm-state is-approved'
                      : 'dm-state'
                  }
                >
                  {ESTADO_LABEL[pedido.status]}
                </span>
              </button>
            ))}
          </div>
        </section>

        <PrivacyRequestForm onCreated={refresh} />
      </div>

      {selectedId === null ? (
        <p className="dm-subtitle">
          Escolha um pedido para ver as provas, verificar o titular e executar o
          direito.
        </p>
      ) : null}

      {detalhe.error ? (
        <div className="dm-alert" role="alert">
          {describeProblem(detalhe.error).message}
        </div>
      ) : null}

      {selectedId !== null && detalhe.data ? (
        <>
          <PrivacyDeliveryStatus request={detalhe.data} />

          <PrivacyResolutionForm
            // A chave é cerca, e não estilo. Sem ela o componente não é
            // remontado ao trocar de pedido, e o estado interno dele atravessa:
            // o código de entrega de USO ÚNICO do titular anterior continua
            // desenhado sob o cabeçalho do pedido novo, e o valor digitado para
            // a correção de um titular seria gravado no registro de outro. O
            // detalhe do pedido anterior fica em cache, então o fragmento nunca
            // desmonta sozinho.
            key={detalhe.data.id}
            request={detalhe.data}
            onResolved={refresh}
            onSubjectVerificationRequired={() => {
              // A API recusou por falta de prova do titular. Isso acontece
              // mesmo quando a tela achava que havia prova: o que estava em
              // cache envelheceu, ou a verificação foi revogada. Abrir o
              // formulário E recarregar o pedido é o que faz a tela parar de
              // afirmar o que o servidor acabou de negar.
              setVerifyOpen(true);
              refresh();
            }}
          />

          {verifyOpen ? (
            <PrivacyVerificationPanel
              requestId={detalhe.data.id}
              onVerified={() => {
                setVerifyOpen(false);
                refresh();
              }}
            />
          ) : detalhe.data.subjectVerifiedAt === null ? (
            <div className="dm-actions">
              <button
                className="dm-action"
                type="button"
                onClick={() => {
                  setVerifyOpen(true);
                }}
              >
                Verificar o titular
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
};
